import React, {useState, useEffect} from 'react';

import {useMutation, useSubscription} from '@apollo/client/react';
import {X, Mail, Lock, AlertCircle, User, Briefcase, CheckCircle} from 'lucide-react';
import {PhoneInput} from '../PhoneInput';
import {gql} from 'types/__generated__';

const SET_USER_EMAIL = gql(`
  mutation SetUserEmail($oldEmail: String!, $newEmail: String!) {
    setUserEmail(oldEmail: $oldEmail, newEmail: $newEmail)
  }
`);

const CHANGE_PASSWORD = gql(`
  mutation ChangePassword($username: String!, $password: String!) {
    changePassword(username: $username, password: $password)
  }
`);

const SET_OWN_USER_REAL_NAME = gql(`
  mutation SetOwnUserRealName($firstName: String, $lastName: String) {
    setOwnUserRealName(firstName: $firstName, lastName: $lastName)
  }
`);

const UPDATE_USER_ACCOUNT_INFO = gql(`
  mutation UpdateUserAccountInfo($input: UserAccountInfoInput!) {
    updateUserAccountInfo(input: $input)
  }
`);

const USER_SUBSCRIPTION = gql(`
  subscription UserUpdatedForSettings($userId: UUID!) {
    user(userId: $userId) {
      id
      username
      email
      firstName
      lastName
      address {
        streetAddress
        city
        postalCode
        country
        mobilePhone
        businessPhone
      }
      workInformation {
        companyName
        department
        jobTitle
      }
    }
  }
`);

interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  address?: {
    streetAddress?: string;
    city?: string;
    postalCode?: string;
    country?: string;
    mobilePhone?: string;
    businessPhone?: string;
  };
  workInformation?: {
    companyName?: string;
    department?: string;
    jobTitle?: string;
  };
}

interface UserSettingsDialogProps {
  user: User;
  isOpen: boolean;
  onClose: () => void;
}

interface ChangesSummary {
  firstName?: {old: string; new: string};
  lastName?: {old: string; new: string};
  email?: {old: string; new: string};
  streetAddress?: {old: string; new: string};
  city?: {old: string; new: string};
  postalCode?: {old: string; new: string};
  country?: {old: string; new: string};
  mobilePhone?: {old: string; new: string};
  businessPhone?: {old: string; new: string};
  companyName?: {old: string; new: string};
  department?: {old: string; new: string};
  jobTitle?: {old: string; new: string};
  password?: boolean;
}

export default function UserSettingsDialog({user, isOpen, onClose}: UserSettingsDialogProps) {
  const [activeTab, setActiveTab] = useState<'profile' | 'work' | 'email' | 'password'>('profile');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [changesSummary, setChangesSummary] = useState<ChangesSummary>({});

  // Profile fields
  const [firstName, setFirstName] = useState(user.firstName || '');
  const [lastName, setLastName] = useState(user.lastName || '');
  const [streetAddress, setStreetAddress] = useState(user.address?.streetAddress || '');
  const [city, setCity] = useState(user.address?.city || '');
  const [postalCode, setPostalCode] = useState(user.address?.postalCode || '');
  const [country, setCountry] = useState(user.address?.country || '');
  const [mobilePhone, setMobilePhone] = useState(user.address?.mobilePhone || '');
  const [businessPhone, setBusinessPhone] = useState(user.address?.businessPhone || '');

  // Work fields
  const [companyName, setCompanyName] = useState(user.workInformation?.companyName || '');
  const [department, setDepartment] = useState(user.workInformation?.department || '');
  const [jobTitle, setJobTitle] = useState(user.workInformation?.jobTitle || '');

  // Email & password fields
  const [newEmail, setNewEmail] = useState(user.email);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pendingUpdate, setPendingUpdate] = useState(false);

  // Subscribe to user updates
  const {data: subscriptionData} = useSubscription(USER_SUBSCRIPTION, {
    variables: {userId: user.id},
    skip: !isOpen, // Only subscribe when modal is open
  });

  // Update form fields when subscription receives updates
  useEffect(() => {
    if (subscriptionData?.user) {
      const updatedUser = subscriptionData.user;
      console.log('📨 User subscription update received:', updatedUser);

      setFirstName(updatedUser.firstName || '');
      setLastName(updatedUser.lastName || '');
      setStreetAddress(updatedUser.address?.streetAddress || '');
      setCity(updatedUser.address?.city || '');
      setPostalCode(updatedUser.address?.postalCode || '');
      setCountry(updatedUser.address?.country || '');
      setMobilePhone(updatedUser.address?.mobilePhone || '');
      setBusinessPhone(updatedUser.address?.businessPhone || '');
      setCompanyName(updatedUser.workInformation?.companyName || '');
      setDepartment(updatedUser.workInformation?.department || '');
      setJobTitle(updatedUser.workInformation?.jobTitle || '');
      setNewEmail(updatedUser.email);

      // If we were waiting for an update, close the modal now
      if (pendingUpdate) {
        console.log('✅ Update confirmed via subscription, closing modal');
        setPendingUpdate(false);
        setSuccess('Changes saved successfully!');
        setTimeout(() => {
          onClose();
        }, 500);
      }
    }
  }, [subscriptionData, pendingUpdate, onClose]);

  // Reset form when user changes
  useEffect(() => {
    setFirstName(user.firstName || '');
    setLastName(user.lastName || '');
    setStreetAddress(user.address?.streetAddress || '');
    setCity(user.address?.city || '');
    setPostalCode(user.address?.postalCode || '');
    setCountry(user.address?.country || '');
    setMobilePhone(user.address?.mobilePhone || '');
    setBusinessPhone(user.address?.businessPhone || '');
    setCompanyName(user.workInformation?.companyName || '');
    setDepartment(user.workInformation?.department || '');
    setJobTitle(user.workInformation?.jobTitle || '');
    setNewEmail(user.email);
  }, [user]);

  const [setUserEmail, {loading: emailLoading}] = useMutation(SET_USER_EMAIL);
  const [changePassword, {loading: passwordLoading}] = useMutation(CHANGE_PASSWORD);
  const [setOwnUserRealName, {loading: nameLoading}] = useMutation(SET_OWN_USER_REAL_NAME);
  const [updateUserAccountInfo, {loading: accountLoading}] = useMutation(UPDATE_USER_ACCOUNT_INFO);

  const detectChanges = (): ChangesSummary => {
    const changes: ChangesSummary = {};

    if (firstName.trim() !== (user.firstName || '')) {
      changes.firstName = {old: user.firstName || '(empty)', new: firstName.trim()};
    }
    if (lastName.trim() !== (user.lastName || '')) {
      changes.lastName = {old: user.lastName || '(empty)', new: lastName.trim()};
    }
    if (newEmail !== user.email) {
      changes.email = {old: user.email, new: newEmail};
    }
    if (streetAddress.trim() !== (user.address?.streetAddress || '')) {
      changes.streetAddress = {old: user.address?.streetAddress || '(empty)', new: streetAddress.trim()};
    }
    if (city.trim() !== (user.address?.city || '')) {
      changes.city = {old: user.address?.city || '(empty)', new: city.trim()};
    }
    if (postalCode.trim() !== (user.address?.postalCode || '')) {
      changes.postalCode = {old: user.address?.postalCode || '(empty)', new: postalCode.trim()};
    }
    if (country.trim() !== (user.address?.country || '')) {
      changes.country = {old: user.address?.country || '(empty)', new: country.trim()};
    }
    if (mobilePhone.trim() !== (user.address?.mobilePhone || '')) {
      changes.mobilePhone = {old: user.address?.mobilePhone || '(empty)', new: mobilePhone.trim()};
    }
    if (businessPhone.trim() !== (user.address?.businessPhone || '')) {
      changes.businessPhone = {old: user.address?.businessPhone || '(empty)', new: businessPhone.trim()};
    }
    if (companyName.trim() !== (user.workInformation?.companyName || '')) {
      changes.companyName = {old: user.workInformation?.companyName || '(empty)', new: companyName.trim()};
    }
    if (department.trim() !== (user.workInformation?.department || '')) {
      changes.department = {old: user.workInformation?.department || '(empty)', new: department.trim()};
    }
    if (jobTitle.trim() !== (user.workInformation?.jobTitle || '')) {
      changes.jobTitle = {old: user.workInformation?.jobTitle || '(empty)', new: jobTitle.trim()};
    }
    if (newPassword.trim()) {
      changes.password = true;
    }

    return changes;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (activeTab === 'email' && (!newEmail || !newEmail.includes('@'))) {
      setError('Please enter a valid email address');
      return;
    }

    if (activeTab === 'password') {
      if (!newPassword || newPassword.length < 8) {
        setError('Password must be at least 8 characters long');
        return;
      }
      if (newPassword !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
    }

    // Detect changes and show confirmation
    const changes = detectChanges();
    if (Object.keys(changes).length === 0) {
      setError('No changes detected');
      return;
    }

    setChangesSummary(changes);
    setShowConfirmation(true);
  };

  const handleConfirmChanges = async () => {
    setShowConfirmation(false);
    setError('');
    setSuccess('Saving changes...');

    try {
      // Update name
      if (changesSummary.firstName || changesSummary.lastName) {
        await setOwnUserRealName({
          variables: {
            firstName: firstName.trim() || null,
            lastName: lastName.trim() || null,
          },
        });
      }

      // Update email
      if (changesSummary.email) {
        await setUserEmail({
          variables: {
            oldEmail: user.email,
            newEmail: newEmail,
          },
        });
      }

      // Update address and work info
      const hasAccountInfoChanges =
        changesSummary.streetAddress ||
        changesSummary.city ||
        changesSummary.postalCode ||
        changesSummary.country ||
        changesSummary.mobilePhone ||
        changesSummary.businessPhone ||
        changesSummary.companyName ||
        changesSummary.department ||
        changesSummary.jobTitle;

      if (hasAccountInfoChanges) {
        const input: any = {};
        if (streetAddress.trim()) input.streetAddress = streetAddress.trim();
        if (city.trim()) input.city = city.trim();
        if (postalCode.trim()) input.postalCode = postalCode.trim();
        if (country.trim()) input.country = country.trim();
        if (mobilePhone.trim()) input.mobilePhone = mobilePhone.trim();
        if (businessPhone.trim()) input.businessPhone = businessPhone.trim();
        if (companyName.trim()) input.companyName = companyName.trim();
        if (department.trim()) input.department = department.trim();
        if (jobTitle.trim()) input.jobTitle = jobTitle.trim();

        await updateUserAccountInfo({
          variables: {input},
        });
      }

      // Update password (password changes don't trigger user subscription)
      if (changesSummary.password) {
        await changePassword({
          variables: {
            username: user.username,
            password: newPassword,
          },
        });
        setNewPassword('');
        setConfirmPassword('');
        // Password changes don't affect user fields, close immediately
        setSuccess('Password changed successfully!');
        setTimeout(() => {
          onClose();
        }, 1500);
        return;
      }

      // Wait for subscription to confirm updates
      setPendingUpdate(true);
    } catch (err: any) {
      console.error('Failed to save changes:', err);
      setError(err.message || 'Failed to save changes');
      setPendingUpdate(false);
    }
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
  };

  if (!isOpen) return null;

  const isLoading = emailLoading || passwordLoading || nameLoading || accountLoading;

  // Confirmation Dialog
  if (showConfirmation) {
    const changeCount = Object.keys(changesSummary).length;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg mx-4">
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <CheckCircle size={24} className="text-blue-600 dark:text-blue-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Confirm Changes</h2>
            </div>
            <button onClick={handleCancelConfirmation} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
              <X size={20} className="text-gray-600 dark:text-gray-400" />
            </button>
          </div>

          <div className="p-6">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              You are about to make {changeCount} change{changeCount !== 1 ? 's' : ''} to your account:
            </p>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {changesSummary.firstName && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">First Name</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {changesSummary.firstName.old} → {changesSummary.firstName.new}
                  </p>
                </div>
              )}
              {changesSummary.lastName && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Last Name</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {changesSummary.lastName.old} → {changesSummary.lastName.new}
                  </p>
                </div>
              )}
              {changesSummary.email && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Email</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {changesSummary.email.old} → {changesSummary.email.new}
                  </p>
                </div>
              )}
              {changesSummary.streetAddress && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Street Address</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {changesSummary.streetAddress.old} → {changesSummary.streetAddress.new}
                  </p>
                </div>
              )}
              {changesSummary.city && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">City</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {changesSummary.city.old} → {changesSummary.city.new}
                  </p>
                </div>
              )}
              {changesSummary.postalCode && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Postal Code</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {changesSummary.postalCode.old} → {changesSummary.postalCode.new}
                  </p>
                </div>
              )}
              {changesSummary.country && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Country</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {changesSummary.country.old} → {changesSummary.country.new}
                  </p>
                </div>
              )}
              {changesSummary.mobilePhone && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Mobile Phone</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {changesSummary.mobilePhone.old} → {changesSummary.mobilePhone.new}
                  </p>
                </div>
              )}
              {changesSummary.businessPhone && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Business Phone</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {changesSummary.businessPhone.old} → {changesSummary.businessPhone.new}
                  </p>
                </div>
              )}
              {changesSummary.companyName && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Company Name</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {changesSummary.companyName.old} → {changesSummary.companyName.new}
                  </p>
                </div>
              )}
              {changesSummary.department && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Department</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {changesSummary.department.old} → {changesSummary.department.new}
                  </p>
                </div>
              )}
              {changesSummary.jobTitle && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Job Title</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {changesSummary.jobTitle.old} → {changesSummary.jobTitle.new}
                  </p>
                </div>
              )}
              {changesSummary.password && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                  <p className="text-sm font-medium text-yellow-900 dark:text-yellow-100">Password</p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">Your password will be changed</p>
                </div>
              )}
            </div>
          </div>

          <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
            <button
              onClick={handleCancelConfirmation}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmChanges}
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Saving...' : 'Confirm & Save'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main Dialog
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">User Settings</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Manage your account information</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
            <X size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('profile')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'profile'
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-b-2 border-blue-600'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <User size={16} className="inline mr-2" />
            Profile
          </button>
          <button
            onClick={() => setActiveTab('work')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'work'
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-b-2 border-blue-600'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <Briefcase size={16} className="inline mr-2" />
            Work Info
          </button>
          <button
            onClick={() => setActiveTab('email')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'email'
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-b-2 border-blue-600'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <Mail size={16} className="inline mr-2" />
            Email
          </button>
          <button
            onClick={() => setActiveTab('password')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'password'
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-b-2 border-blue-600'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <Lock size={16} className="inline mr-2" />
            Password
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Error/Success Messages */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start">
              <AlertCircle size={20} className="text-red-600 dark:text-red-400 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}
          {success && (
            <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <p className="text-sm text-green-800 dark:text-green-200">{success}</p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                {/* Personal Information */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Personal Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">First Name</label>
                      <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="First name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Last Name</label>
                      <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Last name"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Contact Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <PhoneInput label="Mobile Phone" value={mobilePhone} onChange={setMobilePhone} />
                    <PhoneInput label="Business Phone" value={businessPhone} onChange={setBusinessPhone} />
                  </div>
                </div>

                {/* Address Information */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Address</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Street Address</label>
                      <input
                        type="text"
                        value={streetAddress}
                        onChange={(e) => setStreetAddress(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Street address"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Postal Code</label>
                        <input
                          type="text"
                          value={postalCode}
                          onChange={(e) => setPostalCode(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="12345"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">City</label>
                        <input
                          type="text"
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="City"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Country</label>
                        <input
                          type="text"
                          value={country}
                          onChange={(e) => setCountry(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Country"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Work Information Tab */}
            {activeTab === 'work' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Company Name</label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Company name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Department</label>
                  <input
                    type="text"
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Department"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Job Title</label>
                  <input
                    type="text"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Job title"
                  />
                </div>
              </div>
            )}

            {/* Email Tab */}
            {activeTab === 'email' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Current Email</label>
                  <input
                    type="email"
                    value={user.email}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">New Email</label>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter new email address"
                    required
                  />
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    <strong>Note:</strong> Changing your email will update your login email address.
                  </p>
                </div>
              </div>
            )}

            {/* Password Tab */}
            {activeTab === 'password' && (
              <div className="space-y-4">
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    <strong>Security:</strong> Choose a strong password with at least 8 characters.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter new password"
                    minLength={8}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Minimum 8 characters</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Confirm Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Confirm new password"
                    minLength={8}
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Review Changes
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
